import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { IconBadge, SectionPattern } from '@/components/atoms';
import { type IconKey } from '@/content/site';
import { type SectionPatternName } from '@/theme';

type Props = {
  /** The band's texture, reused at card scale. */
  pattern: SectionPatternName;
  /** An oversized watermark — a step number, usually. */
  numeral?: string;
  icon?: IconKey;
};

/**
 * What a media frame draws when its photograph has not been supplied.
 *
 * Not a placeholder. A grey box with a camera glyph tells a visitor that the
 * site is unfinished, which on the page asking them to send money abroad is
 * the worst thing it could say. So this is a composed surface in the site's
 * own language — the band's texture, the step's numeral set as a watermark,
 * the brand-red icon tile the rest of the site already uses — and it is meant
 * to look deliberate for as long as it is there.
 *
 * Decorative throughout: the numeral repeats the card's own heading and the
 * icon repeats its title, so nothing here is announced.
 */
export function MediaPlate({ pattern, numeral, icon }: Props) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        // A touch of brand over the texture, so the plate is not simply the
        // card's own surface with a pattern on it.
        bgcolor: 'rgba(var(--mui-palette-primary-mainChannel) / 0.04)',
      }}
    >
      <SectionPattern name={pattern} />

      {numeral && (
        <Typography
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'h1.fontFamily',
            fontWeight: 800,
            // Steps up with the frame: the plate is a card header in the
            // journey rail and a wide panel head beside the form, and one
            // fixed size would be a stamp in the first and a smudge in the
            // second.
            fontSize: { xs: '5rem', md: '6.5rem' },
            lineHeight: 1,
            color: 'rgba(var(--mui-palette-primary-mainChannel) / 0.1)',
          }}
        >
          {numeral}
        </Typography>
      )}

      {icon && (
        <Box sx={{ position: 'relative' }}>
          <IconBadge name={icon} size={56} tone="tint" shape="circle" />
        </Box>
      )}
    </Box>
  );
}
